import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { RetryIcon, ZoomInIcon, ZoomOutIcon } from '../../assets';
import FileIcon from '../../assets/FileIcon';
import PDFFileIcon from '../../assets/PDFFileIcon';
import { MentionParserFunction } from '../../utils/chat';
import { renderMessageWithMentions } from '../../utils/chat';
import { ImgProxyResizer } from '../../utils/ImageProxyResizer';
import { getTextFromChatMessage } from '../../utils/messages';
import { getOrganizationInitials } from '../../utils/organization';
import { getFullName, getInitials } from '../../utils/User';
import RelativeDate from '../RelativeDate';
import { ShimmerText } from '../Shimmer';
import {
  Container,
  ErrorContainer,
  ErrorText,
  MessageDataContainer,
  MessageDataWrapper,
  MessageFilePreviewContainer,
  MessageImagePreview,
  MessageImagePreviewContainer,
  MessagePDFPreviewContainer,
  MessageText,
  MessageTimestamp,
  MessageUser,
  MessageWrapper,
  OrganizatitonAvatar,
  UserAvatarContainer,
  UserImage,
} from './ChatMessageBubble.styles';

interface ChatMessageBubbleProps {
  user?: {
    name: string;
    lastName: string;
    profileImage: string;
    organization?: {
      name?: string;
      imageUrl?: string;
    };
  };
  message?: {
    text: string;
    timestamp: Date;
    sentAt?: Date;
    image?: string;
    pdf?: string;
    file?: string;
  };
  isLoggedUserSide: boolean;
  isGroupChat?: boolean;
  isSending?: boolean;
  isError?: boolean;
  isShimmer?: boolean;
  isUserConnected?: boolean;
  // Valid CSS `string` that could be used in CSS rules for `min-width`;
  minWidth?: string;
  onResend?: (sentAt: string) => Promise<void>;
  onParseMention?: MentionParserFunction;
}

export const StyledMessageText = styled(MessageText)`
  .mention {
    color: ${({ theme }) => theme.colors.main.c600};
    font-weight: 500;
    text-decoration: none;
  }
`;

const ChatMessageBubble = ({
  user,
  message,
  isLoggedUserSide,
  isGroupChat,
  isSending,
  isError,
  isShimmer,
  isUserConnected,
  minWidth,
  onResend,
  onParseMention,
  ...rest
}: ChatMessageBubbleProps) => {
  const initials = getInitials(user?.name, user?.lastName);
  const [isImageExpanded, setIsImageExpanded] = useState(false);
  const { t } = useTranslation();

  // Extract file URLs from message text
  const imageUrl = message?.image;
  const pdfUrl = message?.pdf;
  const fileUrl = message?.file;

  const messageText = getTextFromChatMessage(message?.text);

  if (isShimmer) {
    return (
      <Container $isLoggedUserSide={isLoggedUserSide} {...rest}>
        <UserAvatarContainer $bordered={false}>
          <ShimmerText $borderRadius="50%" $width="2rem" $height="2rem" />
        </UserAvatarContainer>
        <MessageDataContainer $isLoggedUserSide={isLoggedUserSide} $minWidth={minWidth} $isShimmer>
          <MessageText>
            {[...Array(3)].map(() => (
              <ShimmerText $flex="1" $height="0.75rem" $marginBottom="0.5rem" />
            ))}
          </MessageText>
        </MessageDataContainer>
      </Container>
    );
  }

  const resizedUserImage = new ImgProxyResizer(user?.profileImage).largeAvatarSquaredUrl;

  const handleFileClick = (url: string, fileType: 'image' | 'pdf' | 'file') => {
    if (fileType === 'image') {
      setIsImageExpanded((prev) => !prev);
    } else {
      window.open(url, '_blank');
    }
  };

  return (
    <MessageWrapper>
      <Container $isLoggedUserSide={isLoggedUserSide} $isSending={isSending} {...rest}>
        <UserAvatarContainer $bordered $isConnected={isUserConnected}>
          <UserImage text={initials} src={resizedUserImage} />
          {user.organization && (
            <OrganizatitonAvatar
              text={getOrganizationInitials(user.organization?.name)}
              src={user.organization?.imageUrl}
            />
          )}
        </UserAvatarContainer>
        <MessageDataWrapper>
          <MessageDataContainer $isLoggedUserSide={isLoggedUserSide} $isError={isError}>
            <MessageUser $isSender={isLoggedUserSide}>{getFullName(user?.name, user?.lastName)}</MessageUser>
            <StyledMessageText>{renderMessageWithMentions(messageText, onParseMention)}</StyledMessageText>

            {imageUrl && (
              <MessageImagePreviewContainer
                className={isImageExpanded ? 'is-expanded' : 'is-not-expanded'}
                onClick={() => handleFileClick(imageUrl, 'image')}
              >
                {isImageExpanded ? <ZoomOutIcon /> : <ZoomInIcon />}
                <MessageImagePreview
                  className={isImageExpanded ? 'is-expanded' : 'is-not-expanded'}
                  alt=""
                  src={imageUrl}
                />
              </MessageImagePreviewContainer>
            )}

            {pdfUrl && (
              <MessagePDFPreviewContainer onClick={() => handleFileClick(pdfUrl, 'pdf')}>
                <PDFFileIcon />
              </MessagePDFPreviewContainer>
            )}

            {fileUrl && (
              <MessageFilePreviewContainer onClick={() => handleFileClick(fileUrl, 'file')}>
                <FileIcon />
              </MessageFilePreviewContainer>
            )}

            <MessageTimestamp>
              <RelativeDate date={message?.timestamp} />
            </MessageTimestamp>
          </MessageDataContainer>
          {isError && (
            <ErrorContainer onClick={() => void onResend(message?.sentAt?.toString())}>
              <RetryIcon />
              <ErrorText>{t('components.chat.messageSendError')}</ErrorText>
            </ErrorContainer>
          )}
        </MessageDataWrapper>
      </Container>
    </MessageWrapper>
  );
};

export default ChatMessageBubble;
